import { useEffect, useRef } from "react";
import { isRideCanceled, parseRideFinishedAt } from "../tables/rideStatus";

const SKIPPED_KEY = "ride-cancel:user-canceled-ids";

function readStorage(storage, key) {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function markRideCanceledByUser(rideId) {
  const id = String(rideId);
  const skipped = readStorage(sessionStorage, SKIPPED_KEY) || [];

  if (!skipped.includes(id)) {
    sessionStorage.setItem(SKIPPED_KEY, JSON.stringify([...skipped, id]));
  }
}

function useRideCancelNotifications(rides, options = {}) {
  const { companyAdminId, enabled = true, onCanceled } = options;
  const onCanceledRef = useRef(onCanceled);
  onCanceledRef.current = onCanceled;

  useEffect(() => {
    if (!enabled || !companyAdminId || !Array.isArray(rides)) return;

    const storageKey = `ride-cancel-check:${companyAdminId}`;
    const state = readStorage(localStorage, storageKey) || {
      lastCheckedAt: null,
      notified: [],
    };

    const activeIds = new Set(rides.map((ride) => String(ride.id)));
    state.notified = state.notified.filter((id) => activeIds.has(id));

    const skipped = readStorage(sessionStorage, SKIPPED_KEY) || [];
    const firstRun = !state.lastCheckedAt;

    if (!firstRun) {
      for (const ride of rides) {
        if (!isRideCanceled(ride)) continue;

        const rideId = String(ride.id);
        const finishedAt = parseRideFinishedAt(ride.finished_at);

        if (!finishedAt || finishedAt.getTime() <= state.lastCheckedAt) continue;
        if (state.notified.includes(rideId)) continue;

        if (skipped.includes(rideId)) {
          state.notified.push(rideId);
          sessionStorage.setItem(
            SKIPPED_KEY,
            JSON.stringify(skipped.filter((id) => id !== rideId)),
          );
          continue;
        }

        onCanceledRef.current?.(ride);
        state.notified.push(rideId);
      }
    }

    state.lastCheckedAt = Date.now();
    localStorage.setItem(storageKey, JSON.stringify(state));
  }, [rides, enabled, companyAdminId]);
}

export default useRideCancelNotifications;
